import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { toast } from "react-toastify";
import { fetchCoursesPublic } from "slices/Course/thunk";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import logoSm from "../../assets/images/logo-sm.png";
import CartIcon from "../../Components/Common/CartIcon";

const selectCourseState = (state: RootState) => (state as any).course;

const courseDataSelector = createSelector(selectCourseState, (course) => ({
  courseData: course.courseData,
  singleCourseData: course.singleCourseData,
  loading: course.loading,
}));

const CourseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { courseData, singleCourseData, loading } = useSelector(courseDataSelector);
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);
  const [course, setCourse] = useState<any>(null);
  const [reportDropdownOpen, setReportDropdownOpen] = useState(false);

  useEffect(() => {
    if (id) {
      // Fetch course by ID
      dispatch(
        fetchCoursesPublic({
          page: 1,
          per_page: 10,
          course_id: parseInt(id),
          searchkey: "",
        })
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Find course from courseData or use singleCourseData
    if (id) {
      // Check if singleCourseData has the course
      if (singleCourseData) {
        // If singleCourseData has courses array
        if (singleCourseData.courses && singleCourseData.courses.length > 0) {
          setCourse(singleCourseData.courses[0]);
          return;
        } 
        // If singleCourseData is the course object itself (has id)
        else if (singleCourseData.id) {
          setCourse(singleCourseData);
          return;
        }
      } 
      // Check courseData array
      if (courseData && courseData.length > 0) {
        const foundCourse = courseData.find((c: any) => c.id === parseInt(id));
        if (foundCourse) {
          setCourse(foundCourse);
        }
      }
    }
  }, [id, courseData, singleCourseData]);

  const toggleWeek = (index: number) => {
    setExpandedWeek(expandedWeek === index ? null : index);
  };

  document.title = course ? `${course.course_name} - Amanat` : "Course Details - Amanat";

  if (loading) {
    return (
      <div className="course-detail-page">
        <div className="loading-state">Loading course details...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-detail-page">
        <div className="error-state">Course not found</div>
      </div>
    );
  }

  const currentPrice = course.price || 0;

  return (
    <div className="course-detail-page">
      {/* Header */}
      <header className="course-header">
        <div className="header-content">
          <div className="logo-section">
            <img src={logoSm} alt="Amanat Logo" height="45" />
            <div className="logo-text">
              <h4>Amanat</h4>
              <p>Enabling Financial Well-being for All</p>
            </div>
          </div>
          <nav className="header-nav">
            <a href="https://amanatfintech.com/" target="_blank" rel="noopener noreferrer">Home</a>
            <a href="https://amanatfintech.com/#services" target="_blank" rel="noopener noreferrer">Services</a>
            <a href="https://amanatfintech.com/about/" target="_blank" rel="noopener noreferrer">About</a>
            <a href="https://amanatfintech.com/about/#team" target="_blank" rel="noopener noreferrer">Team</a>
            <a href="https://amanatfintech.com/impact/" target="_blank" rel="noopener noreferrer">Impact</a>
            <Dropdown isOpen={reportDropdownOpen} toggle={() => setReportDropdownOpen(!reportDropdownOpen)} className="report-dropdown">
              <DropdownToggle tag="a" className="report-dropdown-toggle" onClick={(e: React.MouseEvent) => e.preventDefault()}>
                Report
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem tag={Link} to="/order-history" onClick={() => setReportDropdownOpen(false)}>
                  Order History
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <CartIcon />
            <Link to="/order-history">
              <Button className="contact-btn report-btn">
                Report
              </Button>
            </Link>
            <Button 
              className="contact-btn"
              onClick={() => window.open("https://amanatfintech.com/#contact", "_blank", "noopener,noreferrer")}
            >
              Contact Us
            </Button>
          </nav>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <div className="container">
          <a href="https://amanatfintech.com/" target="_blank" rel="noopener noreferrer">Home</a>
          <span className="separator"> &gt; </span>
          <Link to="/explore-courses">Browse</Link>
          <span className="separator"> &gt; </span>
          <span>Courses</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="course-main">
        <div className="container">
          {/* Course Hero Section */}
          <section className="course-hero">
            <div className="hero-image">
              <img 
                src={course.thumbnail_url || "https://via.placeholder.com/600x400"} 
                alt={course.course_name}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://via.placeholder.com/600x400";
                }}
              />
            </div>
            <div className="hero-content">
              <h1 className="course-title">{course.course_name}</h1>
              <p className="course-description">
                {course.description || "This comprehensive program teaches fundamental and advanced data analysis skills."}
              </p>
              
              <div className="course-details">
                <div className="detail-item">
                  <i className="ri-calendar-line"></i>
                  <span>{course.noofweek || "12"} week</span>
                </div>
                <div className="detail-item">
                  <i className="ri-time-line"></i>
                  <span>{course.hourperday || "5"} hrs/day</span>
                </div>
              </div>

              <div className="pricing-section">
                <div className="price-info">
                  <span className="current-price">BD {currentPrice.toFixed(0)}</span>
                </div>
              </div>

              <div className="action-buttons">
                <Button 
                  className="add-to-cart-btn"
                  onClick={() => {
                    const cartItem = {
                      id: course.id,
                      course_name: course.course_name,
                      thumbnail_url: course.thumbnail_url,
                      noofweek: course.noofweek || "12",
                      hourperday: course.hourperday || "5",
                      course_level: course.course_level || "Beginner",
                      price: course.price || 0,
                      originalPrice: course.price || 0, 
                      discount: 0, 
                      quantity: 1
                    };
                    
                    const existingCart = sessionStorage.getItem("cartItems");
                    let cartItems = existingCart ? JSON.parse(existingCart) : [];
                    
                    const existingIndex = cartItems.findIndex((item: any) => item.id === course.id);
                    if (existingIndex !== -1) {
                      toast.info("Already added in the cart");
                      return;
                    }
                    
                    cartItems.push(cartItem);
                    sessionStorage.setItem("cartItems", JSON.stringify(cartItems));
                    window.dispatchEvent(new Event("cartUpdated"));
                    toast.success("Item added to the cart successfully");
                  }}
                >
                  Add to Cart
                </Button>
                <Button 
                  className="buy-now-btn"
                  onClick={() => {
                    const cartItem = {
                      id: course.id,
                      course_name: course.course_name,
                      thumbnail_url: course.thumbnail_url,
                      noofweek: course.noofweek || "12",
                      hourperday: course.hourperday || "5",
                      course_level: course.course_level || "Beginner",
                      price: course.price || 0,
                      originalPrice: course.price || 0,
                      discount: 0,
                      quantity: 1
                    };
                    
                    const existingCart = sessionStorage.getItem("cartItems");
                    let cartItems = existingCart ? JSON.parse(existingCart) : [];
                    
                    const existingIndex = cartItems.findIndex((item: any) => item.id === course.id);
                    if (existingIndex === -1) {
                      // Product not in cart - add it once
                      cartItems.push(cartItem);
                      sessionStorage.setItem("cartItems", JSON.stringify(cartItems));
                      window.dispatchEvent(new Event("cartUpdated"));
                    }
                    // If product already exists in cart, don't change quantity - just redirect
                    navigate("/cart");
                  }}
                >
                  Buy Now
                </Button>
              </div>
            </div>
          </section>

          {course.key_summary && course.key_summary.length > 0 && (
            <section className="course-highlights">
              {course.key_summary.map((highlight: any, index: number) => (
                <div key={index} className="highlight-card">
                  <div className="highlight-icon">
                    {index === 0 && <i className="ri-lightbulb-line"></i>}
                    {index === 1 && <i className="ri-cpu-line"></i>}
                    {index === 2 && <i className="ri-calendar-check-line"></i>}
                    {index === 3 && <i className="ri-target-line"></i>}
                    {index > 3 && <i className="ri-star-line"></i>}
                  </div>
                  <h3 className="highlight-title">{highlight.head_line || "Feature"}</h3>
                  <p className="highlight-text">{highlight.keysummary || highlight.key_summary || ""}</p>
                </div>
              ))}
            </section>
          )}

          {/* Syllabus and Skills Section - Side by Side */}
          <div className="syllabus-skills-wrapper">
            {/* Syllabus Section */}
            {course.syllabus && course.syllabus.length > 0 && (
              <section className="syllabus-section">
                <h2 className="section-title">Syllabus</h2>
                <div className="syllabus-list">
                  {course.syllabus.map((syllabusItem: any, syllabusIndex: number) => {
                    if (!syllabusItem.syllabusdetail || syllabusItem.syllabusdetail.length === 0) {
                      return null;
                    }
                    
                    return syllabusItem.syllabusdetail.map((week: any, weekIndex: number) => {
                      const globalIndex = syllabusIndex * 100 + weekIndex;
                      const isExpanded = expandedWeek === globalIndex;
                      
                      return (
                        <div key={globalIndex} className="syllabus-item">
                          <div 
                            className="syllabus-header"
                            onClick={() => toggleWeek(globalIndex)}
                          >
                            <div className="week-info">
                              <h3 className="week-title">{week.weekname || `Week ${weekIndex + 1}`}</h3>
                              {week.title && <p className="week-subtitle">{week.title}</p>}
                            </div>
                            <i className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line`}></i>
                          </div>
                          {isExpanded && (
                            <div className="syllabus-content">
                              <div className="week-number">{week.weekname || `Week ${weekIndex + 1}`}</div>
                              {week.detail && week.detail.length > 0 && (
                                <ul className="day-list">
                                  {week.detail.map((day: any, dayIndex: number) => (
                                    <li key={dayIndex} className="day-item">
                                      <strong>{day.dayname || `Day ${dayIndex + 1}`}</strong> {day.title || ""}
                                      {day.description && <p className="day-description">{day.description}</p>}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {week.keydeliverable && (
                                <div className="key-deliverable">
                                  <strong>Key Deliverables:</strong> {week.keydeliverable}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })}
                </div>
              </section>
            )}

            {/* Skills Section */}
            {course.skills && course.skills.length > 0 && (
              <section className="skills-section">
                <h2 className="section-title">Skills</h2>
                <div className="skills-container">
                  {course.skills.map((skill: any, index: number) => (
                    <span key={index} className="skill-tag">
                      {skill.skill_name || skill}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CourseDetail;

